import { useState, useMemo } from 'react';
import { Plus, Search, Edit2, Trash2, Tag, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag } from '@/hooks/useTags';
import type { VersionAwareProps } from '@/features/versions/types/props';
import type { TagWithChannelCount } from '@/services/tags';

export function TagTab({ providerId, versionId, regionId, isReadOnly }: VersionAwareProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TagWithChannelCount | null>(null);
  const [tagForm, setTagForm] = useState({ name: '', description: '' });

  // Require region for tag management
  const hasRequiredParams = !!(providerId && versionId && regionId);

  const { data: tags, isLoading, error } = useTags(
    providerId,
    versionId,
    regionId || 0
  );

  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();

  // Filter tags based on search term
  const filteredTags = useMemo(() => {
    if (!tags) return [];
    if (!searchTerm.trim()) return tags;
    
    return tags.filter(tag =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tag.description && tag.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [tags, searchTerm]);

  const handleCreateTag = async () => {
    if (!tagForm.name.trim() || !hasRequiredParams) return;

    try {
      await createTagMutation.mutateAsync({
        name: tagForm.name.trim(),
        description: tagForm.description.trim() || undefined,
        providerId,
        versionId,
        regionId: regionId!
      });
      
      setIsCreateModalOpen(false);
      setTagForm({ name: '', description: '' });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleEditTag = async () => {
    if (!selectedTag || !tagForm.name.trim()) return;

    try {
      await updateTagMutation.mutateAsync({
        id: selectedTag.id,
        name: tagForm.name.trim(),
        description: tagForm.description.trim() || undefined
      });
      
      setIsEditModalOpen(false);
      setSelectedTag(null);
      setTagForm({ name: '', description: '' });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedTag) return;

    try {
      await deleteTagMutation.mutateAsync(selectedTag.id);
      setIsDeleteAlertOpen(false);
      setSelectedTag(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const openCreateModal = () => {
    setTagForm({ name: '', description: '' });
    setIsCreateModalOpen(true);
  };

  const openEditModal = (tag: TagWithChannelCount) => {
    setSelectedTag(tag);
    setTagForm({ name: tag.name, description: tag.description || '' });
    setIsEditModalOpen(true);
  };

  const openDeleteAlert = (tag: TagWithChannelCount) => {
    setSelectedTag(tag);
    setIsDeleteAlertOpen(true);
  };

  if (!hasRequiredParams) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Region ID is required for tag management
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Error loading tags: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Tag className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Tag Management</h2>
          <Badge variant="secondary">{tags?.length || 0} tags</Badge>
        </div>
        
        {!isReadOnly && (
          <Button onClick={openCreateModal}>
            <Plus className="h-4 w-4 mr-2" />
            Create Tag
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
        <Input
          placeholder="Search tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tags Table */}
      {filteredTags.length === 0 ? (
        <EmptyState
          icon={<Tag className="h-12 w-12" />}
          title={searchTerm ? "No tags found" : "No tags created"}
          description={searchTerm ? "Try a different search term" : "Create your first tag to get started"}
        />
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[120px]">Channels</TableHead>
                <TableHead className="w-[150px]">Created</TableHead>
                {!isReadOnly && <TableHead className="w-[100px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTags.map((tag) => (
                <TableRow key={tag.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span>{tag.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">
                      {tag.description || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="flex items-center space-x-1">
                      <Hash className="h-3 w-3" />
                      <span>{tag.channel_count}</span>
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(tag.created_at || '').toLocaleDateString()}
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell>
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(tag)}
                          className="h-8 w-8 p-0"
                          title="Edit tag"
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteAlert(tag)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          title="Delete tag"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Tag Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>
              Create a custom tag to organize channels with specific attributes or terminology.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="tag-name">Tag Name</Label>
              <Input
                id="tag-name"
                placeholder="e.g., Available on streaming, TNT specific..."
                value={tagForm.name}
                onChange={(e) => setTagForm(prev => ({ ...prev, name: e.target.value }))}
                maxLength={100}
              />
            </div>
            
            <div>
              <Label htmlFor="tag-description">Description (Optional)</Label>
              <Textarea
                id="tag-description"
                placeholder="Additional details about this tag..."
                value={tagForm.description}
                onChange={(e) => setTagForm(prev => ({ ...prev, description: e.target.value }))}
                maxLength={500}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTag}
              disabled={!tagForm.name.trim() || createTagMutation.isPending}
            >
              Create Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
            <DialogDescription>
              Update the tag name and description.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-tag-name">Tag Name</Label>
              <Input
                id="edit-tag-name"
                value={tagForm.name}
                onChange={(e) => setTagForm(prev => ({ ...prev, name: e.target.value }))}
                maxLength={100}
              />
            </div>
            
            <div>
              <Label htmlFor="edit-tag-description">Description (Optional)</Label>
              <Textarea
                id="edit-tag-description"
                value={tagForm.description}
                onChange={(e) => setTagForm(prev => ({ ...prev, description: e.target.value }))}
                maxLength={500}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditTag}
              disabled={!tagForm.name.trim() || updateTagMutation.isPending}
            >
              Update Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Tag Alert */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the tag "{selectedTag?.name}"? 
              This will remove it from all {selectedTag?.channel_count} assigned channels. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTag}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTagMutation.isPending}
            >
              Delete Tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}